/*
 * Made-to-order checkout gate (see sections/ud-mto-gate.liquid for what it does and ud-mto-gate.css for the look).
 *
 * Every way into checkout that goes through the page is caught here: the "Check out" button on the cart page, in the cart
 * drawer and in the cart pop-up, and plain links to /checkout. If the cart holds a product with the made-to-order tag and
 * this cart has not already accepted the current agreement version, the pop-up opens and checkout waits for the tick box.
 * The acceptance is saved on the cart (an "attribute"), which Shopify copies onto the order.
 *
 * It cannot stop someone who types /checkout into the address bar or uses a payment app that skips the cart; the
 * confirmation email carries the full agreement for those orders too.
 */
(function () {
  'use strict';

  if (window.__udGate) return;
  window.__udGate = true;

  var root = document.querySelector('[data-ud-gate]');
  var template = root && root.querySelector('template[data-ud-gate-template]');
  if (!root || !template) return;

  var html = document.documentElement;
  var tag = (root.getAttribute('data-tag') || 'Made to Order').toLowerCase();
  var version = root.getAttribute('data-version') || '1';
  var attrName = root.getAttribute('data-attr') || 'Furniture agreement accepted';

  var dialog = null;
  var box = null;
  var goBtn = null;
  var errorEl = null;
  var pendingEl = null;
  var bypass = false;
  var tagCache = {};
  var warmedAt = 0;

  function jsonFetch(url, options) {
    options = options || {};
    options.credentials = 'same-origin';
    options.cache = 'no-store';
    options.headers = Object.assign({ Accept: 'application/json' }, options.headers || {});
    return fetch(url, options).then(function (response) {
      if (!response.ok) throw new Error(url + ' ' + response.status);
      return response.json();
    });
  }

  function getCart() {
    return jsonFetch('/cart.js?ud=' + Date.now());
  }

  function setAttributes(attributes) {
    return jsonFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    });
  }

  // Is this product tagged made to order? Remembered per product for the visit (tags rarely change).
  function isMadeToOrder(handle) {
    if (Object.prototype.hasOwnProperty.call(tagCache, handle)) return Promise.resolve(tagCache[handle]);
    try {
      var stored = window.sessionStorage.getItem('udg:' + handle);
      if (stored !== null) {
        tagCache[handle] = stored === '1';
        return Promise.resolve(tagCache[handle]);
      }
    } catch (e) {}
    return jsonFetch('/products/' + encodeURIComponent(handle) + '.js').then(function (product) {
      var yes = (product.tags || []).some(function (t) {
        return String(t).toLowerCase() === tag;
      });
      tagCache[handle] = yes;
      try {
        window.sessionStorage.setItem('udg:' + handle, yes ? '1' : '0');
      } catch (e) {}
      return yes;
    });
  }

  function cartNeedsAgreement(cart) {
    var seen = {};
    var handles = [];
    (cart.items || []).forEach(function (item) {
      if (item.handle && !seen[item.handle]) {
        seen[item.handle] = true;
        handles.push(item.handle);
      }
    });
    return Promise.all(handles.map(isMadeToOrder)).then(function (flags) {
      return flags.some(Boolean);
    });
  }

  function hasAccepted(cart) {
    var value = cart.attributes && cart.attributes[attrName];
    return typeof value === 'string' && value.indexOf('agreement version ' + version + ')') > -1;
  }

  function stamp() {
    var now = new Date();
    var human;
    try {
      human = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
      }).format(now);
    } catch (e) {
      human = now.toString();
    }
    return human + ' / ' + now.toISOString().replace(/\.\d+Z$/, 'Z');
  }

  // ---------- Letting checkout carry on ----------
  function proceed(el) {
    bypass = true;
    el.classList.remove('udg-checking');
    el.click();
    window.setTimeout(function () {
      bypass = false;
    }, 1500);
  }

  function check(el) {
    el.classList.add('udg-checking');
    getCart()
      .then(function (cart) {
        return cartNeedsAgreement(cart).then(function (needs) {
          if (!needs) {
            // No made-to-order piece in the cart: an old acceptance should not ride along on an unrelated order.
            if (cart.attributes && cart.attributes[attrName]) {
              var clear = {};
              clear[attrName] = '';
              return setAttributes(clear)
                .catch(function () {})
                .then(function () {
                  proceed(el);
                });
            }
            return proceed(el);
          }
          if (hasAccepted(cart)) return proceed(el);
          return openDialog(el);
        });
      })
      .catch(function () {
        // If anything goes wrong we ask rather than let a made-to-order order through unchecked.
        openDialog(el);
      });
  }

  // ---------- The pop-up ----------
  function ensureDialog() {
    if (dialog) return dialog;
    dialog = template.content.cloneNode(true).querySelector('dialog');
    document.body.appendChild(dialog);
    box = dialog.querySelector('[data-udg-box]');
    goBtn = dialog.querySelector('[data-udg-go]');
    errorEl = dialog.querySelector('[data-udg-error]');

    box.addEventListener('change', function () {
      goBtn.disabled = !box.checked;
      errorEl.hidden = true;
    });
    goBtn.addEventListener('click', accept);
    dialog.querySelector('[data-udg-cancel]').addEventListener('click', function () {
      dialog.close();
    });
    dialog.addEventListener('close', function () {
      html.classList.remove('udg-lock');
      if (pendingEl) {
        var el = pendingEl;
        pendingEl = null;
        try {
          el.focus();
        } catch (e) {}
      }
    });
    return dialog;
  }

  function openDialog(el) {
    el.classList.remove('udg-checking');
    pendingEl = el;
    var d = ensureDialog();
    box.checked = false;
    goBtn.disabled = true;
    goBtn.removeAttribute('aria-busy');
    errorEl.hidden = true;
    html.classList.add('udg-lock');
    if (typeof d.showModal === 'function') d.showModal();
    else d.setAttribute('open', '');
    d.querySelector('.udg__scroll').scrollTop = 0;
    d.querySelector('.udg__title').focus();
  }

  function accept() {
    if (!box.checked || goBtn.getAttribute('aria-busy') === 'true') return;
    goBtn.setAttribute('aria-busy', 'true');
    errorEl.hidden = true;
    var value = 'Accepted ' + stamp() + ' (agreement version ' + version + ')';
    var attributes = {};
    attributes[attrName] = value;
    setAttributes(attributes)
      .then(function (cart) {
        if (!cart.attributes || cart.attributes[attrName] !== value) throw new Error('not saved');
        var el = pendingEl;
        pendingEl = null;
        dialog.close();
        if (el) proceed(el);
      })
      .catch(function () {
        goBtn.removeAttribute('aria-busy');
        errorEl.hidden = false;
      });
  }

  // ---------- Catching the checkout buttons ----------
  function checkoutTarget(node) {
    if (!node || !node.closest) return null;
    var button = node.closest('button[name="checkout"], input[name="checkout"]');
    if (button) return button;
    var link = node.closest('a[href]');
    if (link && link.origin === window.location.origin && /^\/checkout(\/|$)/.test(link.pathname)) return link;
    return null;
  }

  document.addEventListener(
    'click',
    function (event) {
      if (bypass) return;
      var el = checkoutTarget(event.target);
      if (!el || el.disabled || (dialog && dialog.contains(el))) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!el.classList.contains('udg-checking')) check(el);
    },
    true
  );

  // Pressing Enter in a cart field submits the cart form with the checkout button as the submitter.
  document.addEventListener(
    'submit',
    function (event) {
      if (bypass) return;
      var submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!submitter.classList.contains('udg-checking')) check(submitter);
    },
    true
  );

  // Look up the cart's products as soon as the checkout button is touched, so the click itself is quick.
  function warm(event) {
    if (!checkoutTarget(event.target) || Date.now() - warmedAt < 5000) return;
    warmedAt = Date.now();
    getCart()
      .then(cartNeedsAgreement)
      .catch(function () {});
  }
  document.addEventListener('pointerover', warm, true);
  document.addEventListener('focusin', warm, true);
  document.addEventListener('touchstart', warm, { capture: true, passive: true });

  window.addEventListener('pageshow', function () {
    bypass = false;
    document.querySelectorAll('.udg-checking').forEach(function (el) {
      el.classList.remove('udg-checking');
    });
  });

  // ---------- Express checkout buttons on the cart page ----------
  // (Product pages are handled in the section: the tag is known when the page is built.)
  var cartFooter = document.getElementById('main-cart-footer');
  if (cartFooter) {
    var refreshTimer = 0;
    var refreshExpress = function () {
      getCart()
        .then(cartNeedsAgreement)
        .then(function (needs) {
          html.setAttribute('data-ud-mto-cart', needs ? '1' : '0');
        })
        .catch(function () {
          html.setAttribute('data-ud-mto-cart', '1');
        });
    };
    refreshExpress();
    if ('MutationObserver' in window) {
      var observer = new MutationObserver(function () {
        window.clearTimeout(refreshTimer);
        refreshTimer = window.setTimeout(refreshExpress, 400);
      });
      var items = document.getElementById('main-cart-items');
      if (items) observer.observe(items, { childList: true, subtree: true });
      observer.observe(cartFooter, { childList: true, subtree: true });
    }
  }
})();
